import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-subtle hover:shadow-subtle-hover hover:bg-primary-hover rounded-lg font-semibold transition-all duration-200",
        destructive:
          "bg-destructive text-destructive-foreground shadow-subtle hover:shadow-subtle-hover hover:bg-destructive/90 rounded-lg focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        outline:
          "border border-border bg-background shadow-subtle hover:shadow-subtle-hover hover:bg-accent hover:text-accent-foreground rounded-lg dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-subtle hover:shadow-subtle-hover hover:bg-secondary/80 rounded-lg font-semibold transition-all duration-200",
        warning:
          "bg-warning text-warning-foreground shadow-subtle hover:shadow-subtle-hover hover:bg-warning/90 rounded-lg font-semibold transition-all duration-200",
        success:
          "bg-success text-success-foreground shadow-subtle hover:shadow-subtle-hover hover:bg-success/90 rounded-lg font-semibold transition-all duration-200",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 rounded-lg transition-all duration-200",
        link: "text-primary underline-offset-4 hover:underline transition-all duration-200",
      },
      size: {
        default: "h-11 px-6 py-3 has-[>svg]:px-5",
        sm: "h-9 px-4 py-2 has-[>svg]:px-3",
        lg: "h-12 px-8 py-3 has-[>svg]:px-6 text-base",
        icon: "size-11 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
